import React, { useState } from 'react'
import { alpha, Alert, Box, Button, ButtonBase, Chip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import StageSection from './StageSection'
import JunkAnswerList from './JunkAnswerList'
import DisabledReason from '../system/DisabledReason'
import type { ThemeAIPersonalDataDecision, ThemeAIPersonalDataRecordRow, ThemeAIScreening } from '../../types/gotypes'
import {
  PERSONAL_DATA_CATEGORY_ORDER,
  PERSONAL_DATA_CODE,
  PERSONAL_DATA_NAME,
  personalDataCategoryLabel,
  personalDataCountLabel,
} from '../../utils/themeAIPersonalData'

export interface PersonalDataRecordsView {
  records: ThemeAIPersonalDataRecordRow[]
  total: number
  offset: number
  pageSize: number
  loading: boolean
  onPageChange: (offset: number) => void
}

export interface PersonalDataReviewStageProps {
  screening: ThemeAIScreening
  records: PersonalDataRecordsView
  /** The kind filter, or undefined for every flagged answer. */
  category?: string
  onCategoryChange: (category: string | undefined) => void
  saving: boolean
  /** Why the decision and Keep are closed to this person (they cannot change the task), or undefined. */
  writeBlockedReason?: string
  onSetDecision: (decision: ThemeAIPersonalDataDecision) => Promise<unknown>
  onSetKept: (recordId: string, kept: boolean) => void
  /** Saves the CSV of the flagged answers' ids and kinds (#488). */
  onDownload: () => Promise<unknown>
  downloading: boolean
  /** Back to Run. */
  onBack: () => void
}

const CHOICES: { decision: Exclude<ThemeAIPersonalDataDecision, ''>; title: string; detail: string }[] = [
  { decision: 'keep', title: 'Do not flag them', detail: 'Code them as usual. Nothing marks them in the results.' },
  {
    decision: 'code',
    title: `Flag them as “${PERSONAL_DATA_NAME.toLowerCase()}”`,
    detail: `Code them as usual and add code ${PERSONAL_DATA_CODE}, “${PERSONAL_DATA_NAME}”, so they can be found in results and tables.`,
  },
]

// The Run step's personal data review (#494): every answer the check found
// personal data in, with the kinds found, two things to do with them, a Keep
// switch for one that is fine, and the download data management acts on. A
// flag only: no choice here changes or removes an answer's text.
const PersonalDataReviewStage: React.FC<PersonalDataReviewStageProps> = ({
  screening,
  records,
  category,
  onCategoryChange,
  saving,
  writeBlockedReason,
  onSetDecision,
  onSetKept,
  onDownload,
  downloading,
  onBack,
}) => {
  const [choice, setChoice] = useState<ThemeAIPersonalDataDecision>(screening.personal_data_decision ?? '')

  const byCategory = screening.personal_data_by_category ?? {}
  const categories = PERSONAL_DATA_CATEGORY_ORDER.filter((key) => (byCategory[key] ?? 0) > 0)
  const flagged = screening.personal_data_count ?? 0

  const handleSave = async () => {
    if (!choice) return
    try {
      await onSetDecision(choice)
      onBack()
    } catch {
      // The hook reports it; stay here so the choice is not lost.
    }
  }

  const handleDownload = async () => {
    try {
      await onDownload()
    } catch {
      // The hook reports it.
    }
  }

  return (
    <StageSection
      title="Personal data"
      description={`${personalDataCountLabel(flagged)} found in ${screening.response_count.toLocaleString()} answers. The answers are never changed: choose whether coding flags them.`}
      actions={<Button color="info" startIcon={<ArrowBackIcon />} onClick={onBack}>Run</Button>}
    >
      {screening.unchecked_count > 0 && (
        <Alert severity="warning">
          {screening.unchecked_count.toLocaleString()} answers could not be checked by the model. The rules still checked them for emails, phone numbers and other patterns, but not for names.
        </Alert>
      )}

      <Box role="radiogroup" aria-label="What to do with answers with personal data" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
        {CHOICES.map((item) => (
          <DisabledReason key={item.decision} reason={writeBlockedReason}>
            <ChoiceCard selected={choice === item.decision} title={item.title} detail={item.detail} onSelect={() => setChoice(item.decision)} />
          </DisabledReason>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <DisabledReason reason={writeBlockedReason}>
          <Button variant="contained" color="info" disabled={!choice || saving} onClick={handleSave}>
            Save decision
          </Button>
        </DisabledReason>
        <Button color="info" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={downloading || flagged === 0}>
          Download list (CSV)
        </Button>
        <Typography variant="caption" color="text.secondary">
          Record and case ids with the kind of data found, for removal at source. No answer text.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} role="group" aria-label="Filter by the kind of personal data">
        <Chip
          label={`All (${flagged.toLocaleString()})`}
          color={category === undefined ? 'info' : 'default'}
          variant={category === undefined ? 'filled' : 'outlined'}
          onClick={() => onCategoryChange(undefined)}
        />
        {categories.map((key) => (
          <Chip
            key={key}
            label={`${personalDataCategoryLabel(key)} (${(byCategory[key] ?? 0).toLocaleString()})`}
            color={category === key ? 'info' : 'default'}
            variant={category === key ? 'filled' : 'outlined'}
            onClick={() => onCategoryChange(key)}
          />
        ))}
      </Box>

      <JunkAnswerList
        {...records}
        writeBlockedReason={writeBlockedReason}
        onSetKept={onSetKept}
        renderTags={(record) => (
          <>
            {record.categories.map((c) => <Chip key={c} size="small" variant="outlined" label={personalDataCategoryLabel(c)} />)}
            <Chip size="small" variant="outlined" label={record.source === 'rule' ? 'Rule' : 'Model'} />
          </>
        )}
      />
    </StageSection>
  )
}

// The same selectable card the junk review uses.
const ChoiceCard: React.FC<{ selected: boolean; title: string; detail: string; onSelect: () => void }> = ({ selected, title, detail, onSelect }) => (
  <ButtonBase
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    sx={(theme) => ({
      display: 'block',
      textAlign: 'left',
      p: 2,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: selected ? 'info.main' : 'divider',
      backgroundColor: selected ? alpha(theme.palette.info.main, 0.06) : 'transparent',
      transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
      '&:hover': { borderColor: selected ? 'info.main' : 'text.secondary' },
      '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 2 },
    })}
  >
    <Typography variant="body1" sx={{ fontWeight: 600 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{detail}</Typography>
  </ButtonBase>
)

export default PersonalDataReviewStage
